import type { ApiResponse } from './types';

let apiToken = '';

/** Detect if we're behind a proxy (e.g. /people/) and prefix API calls */
function getApiBase(): string {
  const path = window.location.pathname;
  // If accessed at /people/*, prefix API calls with /people
  const match = path.match(/^(\/[a-z-]+)\//);
  if (match && match[1] && match[1] !== '/api') return match[1];
  return '';
}

export const API_BASE = getApiBase();

export function setToken(token: string): void {
  apiToken = token;
}

async function request<T>(path: string, opts?: RequestInit): Promise<T> {
  const headers: Record<string, string> = {
    'Content-Type': 'application/json',
    ...(apiToken ? { Authorization: `Bearer ${apiToken}` } : {}),
  };
  // Prefix with proxy base if needed (e.g. /people/api/messages)
  const url = path.startsWith('/api/') ? `${API_BASE}${path}` : path;
  const res = await fetch(url, { ...opts, headers, credentials: 'include' });
  const text = await res.text();
  if (!text) {
    throw new Error(res.ok ? 'Empty response' : `${res.status} ${res.statusText}`);
  }
  let json: ApiResponse<T>;
  try {
    json = JSON.parse(text) as ApiResponse<T>;
  } catch {
    throw new Error(`${res.status} ${res.statusText}`);
  }
  if (json.status === 'error') {
    // Carry the HTTP status so the error toast can build the copyable
    // [HUB-ERR …] block (the message already ends with a server [ref:…]).
    const e = new Error(json.error ?? 'Unknown error') as Error & { status?: number };
    e.status = res.status;
    throw e;
  }
  return json.data as T;
}

export async function get<T>(path: string): Promise<T> {
  return request<T>(path);
}

/** Fetch a binary resource (e.g. an email attachment) as a Blob, carrying the
 *  Bearer token in the header so it never leaks into the URL/history. */
export async function getBlob(path: string): Promise<Blob> {
  const headers: Record<string, string> = {
    ...(apiToken ? { Authorization: `Bearer ${apiToken}` } : {}),
  };
  const url = path.startsWith('/api/') ? `${API_BASE}${path}` : path;
  const res = await fetch(url, { headers, credentials: 'include' });
  if (!res.ok) {
    throw new Error(`${res.status} ${res.statusText}`);
  }
  return res.blob();
}

export async function post<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, {
    method: 'POST',
    body: JSON.stringify(body),
  });
}

export async function put<T>(path: string, body: unknown): Promise<T> {
  return request<T>(path, {
    method: 'PUT',
    body: JSON.stringify(body),
  });
}

export async function del<T>(path: string): Promise<T> {
  return request<T>(path, { method: 'DELETE' });
}

// ── Templates (Mitchell-curated server scaffolds, 2026-04-25) ──
// Shapes mirror src/handlers/templates.rs (TemplateGroup, ServerTemplate).

export interface TemplateGroup {
  id: string;
  label: string;
  icon: string;
  description: string;
  display_order: number;
}

export interface ServerTemplate {
  id: string;
  group_id: string;
  slug: string;
  label: string;
  description: string;
  channels_json: string;
  roles_json: string;
  server_type: 'personal' | 'community' | 'business';
  visibility: 'private' | 'internal' | 'public';
  is_official: number;
  display_order: number;
}

export interface TemplateChannel {
  name: string;
  channel_type?: string;
  category?: string;
  position?: number;
  topic?: string;
}

export function apiTemplateGroups(): Promise<TemplateGroup[]> {
  return get<TemplateGroup[]>('/api/templates/groups');
}

export function apiTemplatesByGroup(gid: string): Promise<ServerTemplate[]> {
  return get<ServerTemplate[]>(`/api/templates/by-group/${encodeURIComponent(gid)}`);
}

export interface CreatedServerLite {
  id: string;
  name: string;
  type: 'personal' | 'community' | 'business';
  visibility: 'private' | 'internal' | 'public';
  icon: string;
  description: string;
  owner_id: string;
  created_at: string;
  updated_at: string;
}

export function apiCreateServerFromTemplate(
  template_id: string,
  name: string,
  owner_id: string,
  visibility?: 'private' | 'internal' | 'public',
): Promise<CreatedServerLite> {
  const body: Record<string, string> = { template_id, name, owner_id };
  if (visibility) body['visibility'] = visibility;
  return post<CreatedServerLite>('/api/servers/from-template', body);
}
